import { loader as monacoLoader } from "@monaco-editor/react"

// Lazy-load Monaco from the bundled npm package. The @monaco-editor/react
// <Editor> component calls loader.init() when it first mounts. We override
// init() to use a dynamic import(), so webpack/rspack code-splits Monaco
// (~2 MB) into a separate chunk that is only fetched when an editor renders.
//
// NOTE: The monaco-editor-webpack-plugin (in webpack.config.js / rspack.config.js)
// still runs at build time to emit web-worker entry points and tree-shake
// language features — it is not affected by the runtime lazy-load here.

/**
 * Monaco v0.54+ ESM always creates workers with `{ type: 'module' }`, but the
 * MonacoWebpackPlugin v7.1.0 generates cross-origin blob workers that use
 * `importScripts()` — which is unavailable in module workers. Fix by providing
 * `MonacoEnvironment.getWorker` (checked before `getWorkerUrl`) that delegates
 * to the plugin's `getWorkerUrl` but creates a **classic** worker instead.
 */
type WorkerUrlFn = (moduleId: string, label: string) => string
type WorkerFactoryFn = (moduleId: string, label: string) => Worker

interface MonacoEnvironmentConfig {
  getWorkerUrl?: WorkerUrlFn
  getWorker?: WorkerFactoryFn
}

function patchWorkerFactory(): void {
  const env = (self as unknown as { MonacoEnvironment?: MonacoEnvironmentConfig })
    .MonacoEnvironment
  if (env?.getWorkerUrl && !env.getWorker) {
    const origGetWorkerUrl: WorkerUrlFn = env.getWorkerUrl.bind(env)
    env.getWorker = function (_moduleId: string, label: string): Worker {
      const url = origGetWorkerUrl(_moduleId, label)
      // Classic worker (no { type: 'module' }) so importScripts() works
      return new Worker(url, { name: label })
    }
  }
}

let monacoPromise: Promise<typeof import("monaco-editor")> | null = null

export function initMonacoLoader(): void {
  monacoLoader.init = () => {
    if (!monacoPromise) {
      monacoPromise = (async () => {
        try {
          const monaco = await import("monaco-editor")
          patchWorkerFactory()
          return monaco
        } catch (err: unknown) {
          monacoPromise = null // Reset cache on failure to allow retry
          throw err instanceof Error ? err : new Error(String(err))
        }
      })()
    }

    // Wrap with cancellation support to match the CancelablePromise interface
    // that @monaco-editor/react expects from loader.init(). The Editor component
    // calls cancel() on unmount and filters rejections where type === "cancelation".
    let canceled = false
    const cancelError = Object.assign(new Error("operation is manually canceled"), {
      type: "cancelation" as const,
    })
    // Capture a local reference that TypeScript knows is non-null (monacoPromise
    // is always set by the block above or by a prior call).
    const pending = monacoPromise
    const wrapped = (async () => {
      try {
        const val = await pending
        if (canceled) {
          throw cancelError
        }
        return val
      } catch (err: unknown) {
        if (canceled) {
          throw cancelError
        }
        throw err instanceof Error ? err : new Error(String(err))
      }
    })()

    ;(wrapped as unknown as { cancel: () => void }).cancel = () => {
      canceled = true
    }

    return wrapped as ReturnType<typeof monacoLoader.init>
  }
}
